import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, ChevronRight, ChevronDown, Sparkles } from 'lucide-react';

const HOVER_OPEN_DELAY = 150;
const HOVER_CLOSE_DELAY = 300;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [activeSection, setActiveSection] = useState('hero');
  const [openMenuId, setOpenMenuId] = useState(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = ['hero', 'services', 'devis', 'produits', 'vitrine', 'avis', 'faq', 'contact'];
      for (const section of [...sections].reverse()) {
        const element = document.getElementById(section);
        if (element && window.scrollY >= element.offsetTop - 100) {
          setActiveSection(section);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    {
      href: '#services',
      label: 'Services',
      id: 'services',
      children: [
        { label: 'Réparation iPhone', to: '/reparation-iphone-montpellier', icon: '📱' },
        { label: 'Réparation Samsung', to: '/reparation-samsung-montpellier', icon: '📱' },
        { label: 'Micro-soudure & carte mère', to: '/micro-soudure-montpellier', icon: '🔬' },
        { label: 'Tablette, ordi, consoles, formations', href: '#services', icon: '🛠️' },
      ],
    },
    { href: '#devis', label: 'Devis & Rachat téléphone', id: 'devis' },
    {
      href: '#produits',
      label: 'Boutique',
      id: 'produits',
      activeIds: ['produits', 'vitrine'],
      groups: [
        {
          title: 'Poses de protection',
          items: [
            { label: 'Verre Trempé', href: '#protection', icon: '🛡️' },
            { label: 'Hydrogel Sur Mesure', href: '#protection', icon: '💧' },
            { label: 'Verre Trempé Caméra', href: '#protection', icon: '📷' },
          ],
        },
        {
          title: 'Accessoires',
          items: [
            { label: 'Chargeurs & Câbles', href: '#accessoires', icon: '🔌' },
            { label: 'Audio', href: '#accessoires', icon: '🎧' },
            { label: 'Énergie', href: '#accessoires', icon: '🔋' },
            { label: 'Coques & Étuis', href: '#accessoires', icon: '📱' },
            { label: 'Supports & Photo', href: '#accessoires', icon: '📸' },
            { label: 'Gadgets & Divers', href: '#accessoires', icon: '🧰' },
          ],
        },
        {
          title: 'Occasion',
          items: [
            { label: "iPhones d'occasion", href: '#vitrine', icon: '📱' },
            { label: 'Rachat de votre téléphone', href: '#devis', icon: '💶' },
          ],
        },
      ],
    },
    { href: '#avis', label: 'Avis', id: 'avis' },
    { href: '#faq', label: 'FAQ', id: 'faq' },
    { href: '#contact', label: 'Contact', id: 'contact' },
  ];

  const closeMenus = () => {
    setIsMobileMenuOpen(false);
    setMobileExpanded(null);
    setOpenMenuId(null);
  };

  const scrollToSection = (href) => {
    if (location.pathname === '/') {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      try { sessionStorage.setItem('allorep:scrollTo', href); } catch {}
      navigate('/');
    }
    setIsMobileMenuOpen(false);
    setMobileExpanded(null);
    setOpenMenuId(null);
  };

  const handleMenuEnter = (id) => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => setOpenMenuId(id), HOVER_OPEN_DELAY);
  };

  const handleMenuLeave = () => {
    if (openTimerRef.current) { clearTimeout(openTimerRef.current); openTimerRef.current = null; }
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpenMenuId(null), HOVER_CLOSE_DELAY);
  };

  const isLinkActive = (link) => {
    if (link.activeIds) return link.activeIds.includes(activeSection);
    return activeSection === link.id;
  };

  return (
    <>
      <header
        data-testid="header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'py-2' : 'py-4'
        }`}
      >
        <div className={`absolute inset-0 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-xl shadow-lg shadow-slate-200/50 border-b border-slate-100' 
            : 'bg-transparent'
        }`} />
        
        {isScrolled && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
        )}

        <div className="container-custom relative">
          <nav className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center group" data-testid="logo-link">
              <div className="relative">
                <img
                  src="/logo.png"
                  alt="AlloRéparations Montpellier — Réparation iPhone, Samsung, MacBook"
                  width="476"
                  height="205"
                  className="h-11 w-auto transition-transform duration-300 group-hover:scale-105"
                  data-testid="logo-image"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center" data-testid="desktop-nav">
              <div className={`flex items-center gap-0.5 px-2 py-1.5 rounded-full transition-all duration-300 ${
                isScrolled
                  ? 'bg-slate-100/80'
                  : 'bg-white/30 backdrop-blur-sm'
              }`}>
                {navLinks.map((link) => {
                  const hasDropdown = link.children || link.groups;
                  const isActive = isLinkActive(link);
                  const isOpen = openMenuId === link.id;
                  const groupCount = link.groups?.length || 0;
                  const megaMenuCols = groupCount >= 3 ? 3 : 2;
                  const megaMenuMinWidth = groupCount >= 3 ? 720 : 480;

                  return (
                    <div
                      key={link.href}
                      className="relative"
                      onMouseEnter={() => hasDropdown && handleMenuEnter(link.id)}
                      onMouseLeave={() => hasDropdown && handleMenuLeave()}
                    >
                      <button
                        onClick={() => scrollToSection(link.href)}
                        className={`relative px-3 py-2 rounded-full font-medium text-[13px] transition-all duration-300 flex items-center gap-1 ${
                          isActive
                            ? 'text-white'
                            : isScrolled
                              ? 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                              : 'text-slate-700 hover:text-slate-900 hover:bg-white/50'
                        }`}
                        data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        {isActive && (
                          <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full shadow-lg shadow-blue-500/30" />
                        )}
                        <span className="relative z-10">{link.label}</span>
                        {hasDropdown && (
                          <ChevronDown
                            size={12}
                            className={`relative z-10 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                          />
                        )}
                      </button>

                      {hasDropdown && (
                        <div
                          className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 transition-all duration-200 z-50 ${
                            isOpen
                              ? 'opacity-100 visible translate-y-0'
                              : 'opacity-0 invisible translate-y-2 pointer-events-none'
                          }`}
                          data-testid={`dropdown-${link.id}`}
                        >
                          <div className="bg-white rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-100 overflow-hidden">
                            <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600" />
                            {link.children && (
                              <div className="p-3 min-w-[260px]">
                                {link.children.map((item) => {
                                  const itemClass = "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm text-slate-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-600 transition-all";
                                  return item.to ? (
                                    <Link
                                      key={item.label}
                                      to={item.to}
                                      onClick={closeMenus}
                                      className={itemClass}
                                    >
                                      <span className="text-lg">{item.icon}</span>
                                      <span className="font-medium">{item.label}</span>
                                    </Link>
                                  ) : (
                                    <button
                                      key={item.label}
                                      onClick={() => scrollToSection(item.href)}
                                      className={itemClass}
                                    >
                                      <span className="text-lg">{item.icon}</span>
                                      <span className="font-medium">{item.label}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            {link.groups && (
                              <div
                                className="p-4 grid gap-6"
                                style={{
                                  gridTemplateColumns: `repeat(${megaMenuCols}, minmax(0, 1fr))`,
                                  minWidth: megaMenuMinWidth,
                                }}
                              >
                                {link.groups.map((group) => (
                                  <div key={group.title}>
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 px-3">
                                      {group.title}
                                    </p>
                                    <div className="space-y-0.5">
                                      {group.items.map((item) => (
                                        <button
                                          key={item.label}
                                          onClick={() => scrollToSection(item.href)}
                                          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm text-slate-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-600 transition-all"
                                        >
                                          <span className="text-base">{item.icon}</span>
                                          <span className="font-medium">{item.label}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:+33983820754"
                className={`group flex items-center gap-2 px-3 py-2 rounded-full transition-all ${
                  isScrolled
                    ? 'bg-slate-100/80 hover:bg-slate-200/80'
                    : 'bg-white/40 backdrop-blur-sm hover:bg-white/60'
                }`}
                data-testid="header-phone"
              >
                <div className="relative">
                  <Phone size={14} className="text-slate-600" />
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                </div>
                <span className="font-medium text-sm text-slate-700">
                  09 83 82 07 54
                </span>
              </a>
              
              <button
                onClick={() => scrollToSection('#devis')}
                className="group relative overflow-hidden px-5 py-2.5 rounded-full font-semibold text-sm text-white transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5"
                data-testid="header-cta-devis"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600" />
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative flex items-center gap-2">
                  <Sparkles size={14} />
                  Devis gratuit
                  <ChevronRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`lg:hidden p-2.5 rounded-xl transition-all ${
                isMobileMenuOpen
                  ? 'bg-slate-900 text-white'
                  : isScrolled
                    ? 'bg-slate-100 text-slate-700'
                    : 'bg-white/30 backdrop-blur-sm text-slate-700'
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
        
        <div
          className={`absolute top-20 left-4 right-4 bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-500 ${
            isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-8 opacity-0'
          }`}
          data-testid="mobile-menu"
        >
          <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
          
          <div className="p-6">
            <div className="space-y-2 mb-6">
              {navLinks.map((link, index) => {
                const hasDropdown = link.children || link.groups;
                const isExpanded = mobileExpanded === link.id;
                const subItems = link.children
                  ? link.children
                  : link.groups
                    ? link.groups.flatMap((g) => g.items.map((i) => ({ ...i, groupTitle: g.title })))
                    : [];
                return (
                  <div key={link.href}>
                    <button
                      onClick={() => {
                        if (hasDropdown) {
                          setMobileExpanded(isExpanded ? null : link.id);
                        } else {
                          scrollToSection(link.href);
                        }
                      }}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl text-left font-medium transition-all ${
                        isLinkActive(link)
                          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                      data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <span>{link.label}</span>
                      {hasDropdown ? (
                        <ChevronDown
                          size={18}
                          className={`transition-transform duration-300 ${
                            isExpanded ? 'rotate-180 text-blue-500' : 'text-slate-400'
                          }`}
                        />
                      ) : (
                        <ChevronRight size={18} className={isLinkActive(link) ? 'text-blue-500' : 'text-slate-400'} />
                      )}
                    </button>

                    {hasDropdown && isExpanded && (
                      <div className="mt-1 ml-3 pl-3 border-l-2 border-blue-100 space-y-0.5">
                        {link.groups ? (
                          link.groups.map((group) => (
                            <div key={group.title} className="pt-2">
                              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1 px-3">
                                {group.title}
                              </p>
                              {group.items.map((item) => (
                                <button
                                  key={item.label}
                                  onClick={() => scrollToSection(item.href)}
                                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-all"
                                >
                                  <span className="text-base">{item.icon}</span>
                                  <span>{item.label}</span>
                                </button>
                              ))}
                            </div>
                          ))
                        ) : (
                          subItems.map((item) =>
                            item.to ? (
                              <Link
                                key={item.label}
                                to={item.to}
                                onClick={closeMenus}
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-all"
                              >
                                <span className="text-base">{item.icon}</span>
                                <span>{item.label}</span>
                              </Link>
                            ) : (
                            <button
                              key={item.label}
                              onClick={() => scrollToSection(item.href)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-all"
                            >
                              <span className="text-base">{item.icon}</span>
                              <span>{item.label}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mb-6" />
            
            <a
              href="tel:+33983820754"
              className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl mb-4"
              data-testid="mobile-phone"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                <Phone size={18} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Appelez-nous</p>
                <p className="font-semibold text-slate-900">09 83 82 07 54</p>
              </div>
            </a>
            
            <button
              onClick={() => scrollToSection('#devis')}
              className="w-full py-4 rounded-2xl font-semibold text-white bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2"
              data-testid="mobile-cta-devis"
            >
              <Sparkles size={18} />
              Obtenir un devis gratuit
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
